// Presentation helpers. Portal timestamps are UNIX SECONDS.

export function formatDate(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "—";
  const d = new Date(unixSeconds * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDateShort(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "—";
  const d = new Date(unixSeconds * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** Relative "x ago" for recency; falls back to a short date for older items. */
export function formatRelative(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "—";
  const ms = unixSeconds * 1000;
  if (Number.isNaN(ms)) return "—";
  const diffSec = Math.round((Date.now() - ms) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return "just now";
  const mins = Math.round(diffSec / 60);
  if (Math.abs(mins) < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (Math.abs(hrs) < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (Math.abs(days) < 7) return `${days}d ago`;
  return formatDateShort(unixSeconds);
}

// ----- ticket status -------------------------------------------------------
// The portal API returns the raw `status_id` FK (no joined label). For freshly
// provisioned tenants these ids are stable, code-keyed seed values
// (`tst_open` / `tst_pending` / `tst_solved` / `tst_closed`, migration 0004), so
// we map them to a friendly label + a category used for badge coloring. Unknown
// ids degrade to a humanized fallback so custom statuses still render.

type StatusTone = "open" | "pending" | "solved" | "closed" | "neutral";

interface StatusMeta {
  label: string;
  tone: StatusTone;
}

const STATUS_BY_ID: Record<string, StatusMeta> = {
  tst_open: { label: "Open", tone: "open" },
  tst_pending: { label: "Pending", tone: "pending" },
  tst_solved: { label: "Solved", tone: "solved" },
  tst_closed: { label: "Closed", tone: "closed" },
};

export function ticketStatus(statusId: string | null | undefined): StatusMeta {
  if (statusId && STATUS_BY_ID[statusId]) return STATUS_BY_ID[statusId];
  // Fallback: derive a readable label from an unknown/custom id.
  const raw = (statusId ?? "").replace(/^tst_/, "").replace(/[_-]+/g, " ").trim();
  const label = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Status";
  return { label, tone: "neutral" };
}
